<script setup lang="ts">
import { ref, computed, onMounted, nextTick, watch } from "vue";
import {
  PlusIcon,
  SparklesIcon,
  CheckCircleIcon,
  CollectionIcon,
} from "@heroicons/vue/outline";
import { useDilemmasStore } from "@/stores/modules/dilemmas/dilemmas";
import TopNavigation from "@/components/TopNavigation.vue";
import KekkleFilterTabs, {
  type FilterTabOption,
} from "@/components/ui/KekkleFilterTabs.vue";
import KekkleCarousel from "@/components/ui/KekkleCarousel.vue";
import KekkleEmptyState from "@/components/ui/KekkleEmptyState.vue";
import DilemmaListItem from "./components/DilemmaListItem.vue";
import AddDilemmaBottomSheet from "./components/AddDilemmaBottomSheet.vue";
import { useDilemmaManagement } from "@/composables/useDilemmaManagement";
import { useI18n } from "vue-i18n";

type Props = {
  dilemmaId?: string;
};

type TabId = "all" | "unanswered" | "answered";

const props = defineProps<Props>();

const dilemmasStore = useDilemmasStore();
const { addDilemmaBottomSheetOpen } = useDilemmaManagement();
const { t } = useI18n();

const activeTab = ref<TabId>("all");

const unansweredCount = computed(() => dilemmasStore.unansweredDilemmas.length);
const answeredCount = computed(() => dilemmasStore.answeredDilemmas.length);
const totalCount = computed(() => dilemmasStore.allDilemmas.length);

const tabOptions = computed<FilterTabOption<TabId>[]>(() => [
  {
    id: "all",
    label: t("pages.dilemmas.tabs.all", "All"),
    count: totalCount.value,
    icon: CollectionIcon,
    variant: "purple",
  },
  {
    id: "unanswered",
    label: t("pages.dilemmas.tabs.unanswered", "Unanswered"),
    count: unansweredCount.value,
    icon: SparklesIcon,
    variant: "yellow",
  },
  {
    id: "answered",
    label: t("pages.dilemmas.tabs.answered", "Answered"),
    count: answeredCount.value,
    icon: CheckCircleIcon,
    variant: "blue",
  },
]);

onMounted(async () => {
  dilemmasStore.subscribeToDilemmas();
  if (props.dilemmaId) {
    await nextTick();
    scrollToDilemma(props.dilemmaId);
  }
});

watch(
  () => props.dilemmaId,
  (newId) => {
    if (newId) {
      scrollToDilemma(newId);
    }
  },
);

watch(activeTab, () => {
  dilemmasStore.clearRecentlyVoted();
});

function scrollToDilemma(id: string): void {
  setTimeout(() => {
    const el = document.getElementById("dilemma-" + id);
    if (el) {
      el.scrollIntoView({ behavior: "smooth", block: "center" });
      el.classList.add("ring-4", "ring-kekkle-yellow");
      setTimeout(
        () => el.classList.remove("ring-4", "ring-kekkle-yellow"),
        2000,
      );
    }
  }, 500);
}
</script>

<template>
  <TopNavigation
    previous-route="Games"
    :title="$t('pages.dilemmas.title', 'Dilemmas')"
    button-name="button"
  />

  <div class="relative container mx-auto min-h-screen px-3 pt-24 pb-28">
    <!-- Generic Reusable Filter Tabs -->
    <div
      v-if="!dilemmasStore.loadingInitialDilemmas && totalCount > 0"
      class="mb-6"
    >
      <KekkleFilterTabs v-model="activeTab" :options="tabOptions" />
    </div>

    <!-- Loading Skeleton State -->
    <div
      v-if="dilemmasStore.loadingInitialDilemmas"
      class="mt-4 flex flex-col gap-6"
    >
      <div v-for="i in 2" :key="i" class="flex flex-col gap-3">
        <div
          class="h-6 w-40 animate-pulse rounded-sm bg-gray-300 dark:bg-slate-700"
        ></div>
        <div
          class="h-64 w-full animate-pulse rounded-2xl border-2 border-gray-200 bg-gray-100 dark:border-slate-800 dark:bg-kekkle-dark-light"
        ></div>
      </div>
    </div>

    <!-- Empty Group State -->
    <KekkleEmptyState
      v-else-if="totalCount === 0"
      class="mt-8"
      emoji="🤔"
      :title="$t('pages.dilemmas.no_dilemmas', 'No dilemmas yet!')"
      :description="
        $t(
          'pages.dilemmas.empty_group_description',
          'Be the first to create a tough group decision or funny scenario.',
        )
      "
      :action-text="$t('pages.dilemmas.create_button', 'Create Dilemma')"
      :action-icon="PlusIcon"
      @action="addDilemmaBottomSheetOpen = true"
    />

    <!-- Generic Reusable Carousels Section -->
    <div v-else class="flex flex-col gap-10">
      <!-- Unanswered Dilemmas Carousel -->
      <KekkleCarousel
        v-if="activeTab === 'all' || activeTab === 'unanswered'"
        :title="
          $t('pages.dilemmas.unanswered_section_title', 'Unanswered Dilemmas')
        "
        :badge-count="unansweredCount"
        :items="dilemmasStore.unansweredDilemmas"
        :empty-text="
          $t(
            'pages.dilemmas.all_answered_empty',
            'You\'ve answered all dilemmas! Check back when your group posts new ones.',
          )
        "
        variant="yellow"
      >
        <template #icon>
          <SparklesIcon class="h-4 w-4" />
        </template>
        <template #item="{ item }">
          <DilemmaListItem :dilemma="item" />
        </template>
      </KekkleCarousel>

      <!-- Answered Dilemmas Carousel -->
      <KekkleCarousel
        v-if="activeTab === 'all' || activeTab === 'answered'"
        :title="
          $t('pages.dilemmas.answered_section_title', 'Answered & Results')
        "
        :badge-count="answeredCount"
        :items="dilemmasStore.answeredDilemmas"
        :empty-text="
          $t(
            'pages.dilemmas.no_answered_empty',
            'No answered dilemmas yet. Vote on an unanswered dilemma above to see results here!',
          )
        "
        variant="blue"
      >
        <template #icon>
          <CheckCircleIcon class="h-4 w-4" />
        </template>
        <template #item="{ item }">
          <DilemmaListItem :dilemma="item" />
        </template>
      </KekkleCarousel>
    </div>

    <!-- Fixed Floating Action Button for creating a Dilemma (No navigation bar on Dilemmas route) -->
    <div
      class="pointer-events-none fixed bottom-6 left-0 z-20 flex w-full justify-center"
    >
      <div class="flex w-full max-w-xl justify-end px-5">
        <div
          class="pointer-events-auto h-auto transform cursor-pointer rounded-lg border-2 border-kekkle-yellow-dark bg-kekkle-yellow-light p-1 shadow-yellow-hard-sm transition-all duration-500 ease-in-out hover:translate-y-1 hover:bg-kekkle-yellow hover:shadow-none"
          @click="addDilemmaBottomSheetOpen = true"
        >
          <div
            class="align-center flex flex-row content-center items-center justify-center gap-x-2 px-2 py-1"
          >
            <p class="text-md h-min font-medium text-kekkle-yellow-dark">
              {{ $t("pages.dilemmas.create_button", "Create Dilemma") }}
            </p>
            <PlusIcon class="w-5 text-kekkle-yellow-dark" />
          </div>
        </div>
      </div>
    </div>

    <!-- Bottom Sheet for Dilemma Creation -->
    <AddDilemmaBottomSheet v-model:open="addDilemmaBottomSheetOpen" />
  </div>
</template>
